import React from 'react';
import { Field, Dropdown, NumericInput, TextOutput } from '../../../Core';
import { useDispatch, useSelector } from 'react-redux';
import { WorksheetGridFieldSection } from '../../Core';
import { HorizontalGrid } from '../../../Layout';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import deepEquals from '../../../../Lib/Object/deepEquals';

let MeasurementsSection = ({ restrictToOnePortion, prefixName, createWorksheetSelector }) => {
  const recordInitiallyPreparedMass = useSelector(createWorksheetSelector('Preparation.InitiallyPreparedMassRecorded'));
  const driedPriorToSoaking = useSelector(createWorksheetSelector('Preparation.AggregateOvenDriedPriorToSoaking'));
  const sieves = useSelector(createWorksheetSelector('Equipment.AllSieves'), deepEquals) || [];
  const testPortions = useSelector(createWorksheetSelector('TestPortions'), deepEquals) || [];
  const dispatch = useDispatch();

  const handleGridCreateCol = () =>
    dispatch(actions.addArrayEntry({ name: prefixName(`TestPortions`), index: testPortions.length, sync: true }));
  const handleGridRemoveCol = (index) =>
    dispatch(actions.removeArrayEntry({ name: prefixName(`TestPortions`), index, sync: true }));

  const confirmPortionRemove = ({ name: i }) => {
    //TODO: Implement this properly.
    return rows.some((r) => !(testPortions[i][r.fieldName] === undefined || testPortions[i][r.fieldName] === null));
  };

  const nominalMaxAggSieveOptions = (i) => {
    if (sieves.some((s) => !s.Size || s.Size.uom !== sieves[0].Size.uom))
      throw new Error('Cannot compare sieves sizes. Sizes are missing or are defined with inconsistent units.');

    let importedMinAggregateSieve = testPortions[i].IsMinimumAggregateSizeImported
      ? sieves.find((s) => s.Id === testPortions[i].MinimumAggregateSize)
      : null;
    let validSieves = importedMinAggregateSieve
      ? sieves.filter((s) => Number(s.Size.value) > Number(importedMinAggregateSieve.Size.value))
      : sieves.slice(0, -1);
    return [{ value: 'Unknown', label: 'Unknown' }, ...validSieves.map((s) => ({ value: s.Id, label: s.Caption }))];
  };

  const minAggSieveOptions = (i) => {
    if (sieves.some((s) => !s.Size || s.Size.uom !== sieves[0].Size.uom))
      throw new Error('Cannot compare sieves sizes. Sizes are missing or are defined with inconsistent units.');

    let maxAggregateSieve = sieves.find((s) => s.Id === testPortions[i].NominalMaximumAggregateSize);
    let validSieves = maxAggregateSieve
      ? sieves.filter((s) => Number(s.Size.value) < Number(maxAggregateSieve.Size.value))
      : sieves;

    return validSieves.map((s) => ({ value: s.Id, label: s.Caption }));
  };

  const rows = [
    {
      fieldName: 'NominalMaximumAggregateSize',
      label: 'Nominal Max. Aggregate Size',
      get cellContentForCol() {
        return (i) => (
          <Field
            input={<Dropdown />}
            name={`TestPortions[${i}].${this.fieldName}`}
            options={nominalMaxAggSieveOptions(i)}
            simpleValue
            disabled={i > 0}
            label={this.label}
          />
        );
      },
    },
    {
      fieldName: 'MinimumAggregateSize',
      label: 'Min. Aggregate Size',
      get cellContentForCol() {
        return (i) => (
          <Field
            input={<Dropdown />}
            name={`TestPortions[${i}].${this.fieldName}`}
            options={minAggSieveOptions(i)}
            disabled={testPortions[i].IsMinimumAggregateSizeImported}
            simpleValue
            label={this.label}
          />
        );
      },
    },
    {
      fieldName: 'ProportionOfSample',
      label: 'Proportion of Sample (%)',
      hidden: testPortions.length <= 1,
      get cellContentForCol() {
        return (i) => (
          <Field input={<NumericInput />} name={`TestPortions[${i}].${this.fieldName}`} label={this.label} />
        );
      },
    },
    {
      fieldName: 'TareMass',
      label: 'Tare Mass (g)',
      get cellContentForCol() {
        return (i) => (
          <Field input={<NumericInput />} name={`TestPortions[${i}].${this.fieldName}`} label="Tare Mass" />
        );
      },
    },
    {
      fieldName: 'TareAndInitiallyPreparedMass',
      label: 'Tare and Initially Prepared Mass (g)',
      hidden: !recordInitiallyPreparedMass,
      get cellContentForCol() {
        return (i) => (
          <Field
            input={<NumericInput />}
            name={`TestPortions[${i}].${this.fieldName}`}
            label="Tare and Initially Prepared Mass"
          />
        );
      },
    },
    {
      fieldName: 'InitiallyPreparedMass',
      label: 'Initially Prepared Mass (g)',
      hidden: !recordInitiallyPreparedMass,
      get cellContentForCol() {
        return (i) => (
          <Field input={<TextOutput />} name={`TestPortions[${i}].${this.fieldName}`} label="Initially Prepared Mass" />
        );
      },
    },
    {
      fieldName: 'TareAndSSDMassInAir',
      label: 'Tare and SSD Mass in Air (g)',
      get cellContentForCol() {
        return (i) => (
          <Field
            input={<NumericInput />}
            name={`TestPortions[${i}].${this.fieldName}`}
            label="Tare and SSD Mass in Air"
          />
        );
      },
    },
    {
      fieldName: 'SSDMassInWater',
      label: 'SSD Mass in Water (g)',
      get cellContentForCol() {
        return (i) => (
          <Field input={<NumericInput />} name={`TestPortions[${i}].${this.fieldName}`} label="SSD Mass in Water" />
        );
      },
    },
    {
      fieldName: 'WaterTemperatureReading',
      label: 'Water Temperature (°C)',
      get cellContentForCol() {
        return (i) => (
          <Field input={<NumericInput />} name={`TestPortions[${i}].${this.fieldName}`} label="Water Temperature" />
        );
      },
    },
    {
      fieldName: 'WaterTemperatureCorrected',
      label: 'Corrected Temperature (°C)',
      get cellContentForCol() {
        return (i) => (
          <Field input={<TextOutput />} name={`TestPortions[${i}].${this.fieldName}`} label="Corrected Temperature" />
        );
      },
    },
    {
      fieldName: 'TareAndOvenDryMass',
      label: 'Tare and Oven Dry Mass (g)',
      get cellContentForCol() {
        return (i) => (
          <Field
            input={<NumericInput />}
            name={`TestPortions[${i}].${this.fieldName}`}
            disabled={testPortions[i].IsOvenDryMassImported}
            label="Tare and Oven Dry Mass"
          />
        );
      },
    },
    {
      fieldName: 'OvenDryMass',
      label: 'Oven Dry Mass (g)',
      get cellContentForCol() {
        return (i) => (
          <Field input={<TextOutput />} name={`TestPortions[${i}].${this.fieldName}`} label="Oven Dry Mass" />
        );
      },
    },
    {
      fieldName: 'SpecificGravityOvenDry',
      label: 'Specific Gravity (OD)',
      hidden: restrictToOnePortion,
      get cellContentForCol() {
        return (i) => <Field input={<TextOutput />} name={`TestPortions[${i}].${this.fieldName}`} label={this.label} />;
      },
    },
    {
      fieldName: 'SpecificGravitySSD',
      label: 'Specific Gravity (SSD)',
      hidden: restrictToOnePortion,
      get cellContentForCol() {
        return (i) => <Field input={<TextOutput />} name={`TestPortions[${i}].${this.fieldName}`} label={this.label} />;
      },
    },
    {
      fieldName: 'ApparentSpecificGravity',
      label: 'Apparent Specific Gravity',
      hidden: restrictToOnePortion,
      get cellContentForCol() {
        return (i) => <Field input={<TextOutput />} name={`TestPortions[${i}].${this.fieldName}`} label={this.label} />;
      },
    },
    {
      fieldName: 'Absorption',
      label: 'Absorption (%)',
      hidden: restrictToOnePortion,
      get cellContentForCol() {
        return (i) => <Field input={<TextOutput />} name={`TestPortions[${i}].${this.fieldName}`} label={this.label} />;
      },
    },
  ];

  const moveRowToNewPosition = (rows, targetRowFieldName, newIndex) => {
    rows.splice(
      newIndex,
      0,
      rows.splice(
        rows.findIndex((r) => r.fieldName === targetRowFieldName),
        1
      )[0]
    );
  };

  if (driedPriorToSoaking) {
    // Move TareAndOvenDryMass and OvenDryMass rows above TareAndSSDMassInAir.
    moveRowToNewPosition(
      rows,
      'TareAndOvenDryMass',
      rows.findIndex((r) => r.fieldName === 'TareAndSSDMassInAir')
    );
    moveRowToNewPosition(
      rows,
      'OvenDryMass',
      rows.findIndex((r) => r.fieldName === 'TareAndSSDMassInAir')
    );
  }

  return testPortions ? (
    <WorksheetGridFieldSection name="TestPortions">
      <HorizontalGrid
        cornerHeader={restrictToOnePortion ? null : 'Test Portion ID'}
        rowHeaders={rows.map((r) => r.label)}
        rowHeaderWidth={230}
        colHeaders={restrictToOnePortion ? null : Array.from(Array(testPortions.length), (_, i) => i + 1)}
        data={testPortions.map((_, i) => rows.map((r) => r.cellContentForCol(i)))}
        colWidths={130}
        onCreateCol={restrictToOnePortion ? null : handleGridCreateCol}
        onRemoveCol={restrictToOnePortion ? null : handleGridRemoveCol}
        removeConfirm={{
          confirm: confirmPortionRemove,
          confirmTitle: 'Portion Deletion',
          confirmText: 'Are you sure you want to delete the selected portion?',
          confirmButtonText: 'Delete Portion',
        }}
        hiddenRows={rows.map((r, i) => (r.hidden ? i : null))}
      />
    </WorksheetGridFieldSection>
  ) : null;
};

MeasurementsSection = withInjectedProps(MeasurementsSection);

export { MeasurementsSection };
export default MeasurementsSection;
